<template>
  <div class="world-editor-toolbar">
    <section class="tool-group">
      <header class="rail-head">
        <h4 class="gm-label">Build tools</h4>
      </header>
      <div class="tool-grid">
        <button
            v-for="tool in railTools"
            :key="tool.id"
            :class="['tool-btn', { 'is-active': selectedTool === tool.id }]"
            :aria-pressed="selectedTool === tool.id"
            :title="tool.description"
            @click="selectTool(tool.id)"
        >
          <svg class="tool-icon" viewBox="0 0 20 20" aria-hidden="true">
            <path v-for="(d, i) in tool.paths" :key="i" :d="d"/>
          </svg>
          <span class="tool-name">{{ tool.name }}</span>
        </button>
      </div>
    </section>

    <!-- What the selected tool actually does, rather than leaving it to a tooltip -->
    <p v-if="selectedToolConfig" class="tool-hint">
      <span class="tool-hint__label">{{ selectedToolConfig.name }}</span>
      {{ selectedToolConfig.instructions }}
    </p>

    <section class="tool-group">
      <header class="rail-head">
        <h4 class="gm-label">Connections</h4>
      </header>
      <button
          :class="['switch-row', { 'is-on': autoConnectEnabled }]"
          :aria-pressed="autoConnectEnabled"
          :title="autoConnectEnabled
            ? 'A new room connects to the selected room as soon as it is created.'
            : 'New rooms are created unconnected. Use Connect rooms to link them.'"
          @click="toggleAutoConnect"
      >
        <span class="switch-text">
          <span class="switch-name">Connect on create</span>
          <span class="switch-note">{{ autoConnectEnabled ? 'New rooms link to the selection' : 'New rooms stay unlinked' }}</span>
        </span>
        <span class="switch-track"><span class="switch-knob"></span></span>
      </button>
    </section>
  </div>
</template>

<script>
import {WorldEditorTool} from '@/models/WorldEditorModels.ts';

export default {
  name: 'WorldEditorToolbar',
  props: {
    selectedTool: {
      type: String,
      default: WorldEditorTool.SELECT
    }
  },
  emits: ['tool-selected', 'auto-connect-toggled'],
  data() {
    return {
      // Auto-connect toggle state
      autoConnectEnabled: true, // Default to enabled for convenience

      // Tool options
      connectionOptions: {
        vertical: false
      },
      roomOptions: {
        type: 'default'
      },
      entityOptions: {
        mode: 'single'
      },

      // Tool definitions. Icons are path data rather than an icon font or
      // emoji, so they inherit the rail's colour and stay crisp at 16px.
      basicTools: [
        {
          id: WorldEditorTool.SELECT,
          name: 'Select',
          paths: ['M4 3l12 6-5 1.4L13 15l-2 .9-2-4.6L4.8 15z'],
          description: 'Select a room and inspect it',
          instructions: 'Click a room to select it. Hold Ctrl to add rooms to the selection.'
        }
      ],

      roomTools: [
        {
          id: WorldEditorTool.CREATE_ROOM,
          name: 'Create',
          paths: ['M3.5 3.5h13v13h-13z', 'M10 7v6', 'M7 10h6'],
          description: 'Create a room',
          instructions: 'Click an empty cell to place a room there.'
        },
        {
          id: WorldEditorTool.DELETE_ROOM,
          name: 'Delete',
          paths: ['M4 6h12', 'M8 6V4h4v2', 'M5.5 6l.8 10h7.4l.8-10', 'M8.5 9v4', 'M11.5 9v4'],
          description: 'Delete a room',
          instructions: 'Click a room to delete it. Its connections go with it.'
        },
        {
          id: WorldEditorTool.MOVE_ROOM,
          name: 'Move',
          paths: ['M10 3v14', 'M3 10h14', 'M10 3l-2.2 2.2M10 3l2.2 2.2', 'M10 17l-2.2-2.2M10 17l2.2-2.2', 'M3 10l2.2-2.2M3 10l2.2 2.2', 'M17 10l-2.2-2.2M17 10l-2.2 2.2'],
          description: 'Move a room to another cell',
          instructions: 'Drag a room to the cell you want it in.'
        },
        {
          id: WorldEditorTool.CONNECT_ROOMS,
          name: 'Connect',
          paths: ['M8.2 11.8a3 3 0 010-4.2l2.1-2.1a3 3 0 014.2 4.2l-1 1', 'M11.8 8.2a3 3 0 010 4.2l-2.1 2.1a3 3 0 01-4.2-4.2l1-1'],
          description: 'Link two rooms',
          instructions: 'Click the first room, then the second, to link them.'
        },
        {
          id: WorldEditorTool.DISCONNECT_ROOMS,
          name: 'Disconnect',
          paths: ['M8.2 11.8a3 3 0 010-4.2l1.4-1.4', 'M11.8 8.2a3 3 0 010 4.2l-1.4 1.4', 'M4 4l12 12'],
          description: 'Remove a link between rooms',
          instructions: 'Click a connection line to remove it.'
        }
      ],

      entityTools: [
        {
          id: WorldEditorTool.SPAWN_ITEM,
          name: 'Spawn Item',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Add items to rooms',
          instructions: 'Select a room, then pick an item from the Library.'
        },
        {
          id: WorldEditorTool.EDIT_ITEM,
          name: 'Edit Item',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Edit existing items',
          instructions: 'Click an item in a room to edit its properties.'
        },
        {
          id: WorldEditorTool.SPAWN_NPC,
          name: 'Spawn NPC',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Add NPCs to rooms',
          instructions: 'Select a room, then pick a template from the Library.'
        },
        {
          id: WorldEditorTool.EDIT_NPC,
          name: 'Edit NPC',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Edit existing NPCs',
          instructions: 'Click an NPC in a room to edit its properties.'
        },
        {
          id: WorldEditorTool.SPAWN_ANOMALY,
          name: 'Spawn Anomaly',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Add anomalies to rooms',
          instructions: 'Select a room, then add an anomaly from the spawner panel.'
        },
        {
          id: WorldEditorTool.EDIT_ANOMALY,
          name: 'Edit Anomaly',
          paths: ['M3.5 3.5h13v13h-13z'],
          description: 'Edit existing anomalies',
          instructions: 'Click an anomaly in a room to edit its properties.'
        }
      ]
    };
  },
  computed: {
    allTools() {
      return [...this.basicTools, ...this.roomTools, ...this.entityTools];
    },

    railTools() {
      return [...this.basicTools, ...this.roomTools];
    },

    selectedToolConfig() {
      return this.allTools.find(tool => tool.id === this.selectedTool);
    },

    isConnectionTool() {
      return this.selectedTool === WorldEditorTool.CONNECT_ROOMS;
    },

    isRoomCreationTool() {
      return this.selectedTool === WorldEditorTool.CREATE_ROOM;
    },

    isEntityTool() {
      return [
        WorldEditorTool.SPAWN_ITEM,
        WorldEditorTool.SPAWN_NPC,
        WorldEditorTool.SPAWN_ANOMALY
      ].includes(this.selectedTool);
    }
  },
  methods: {
    selectTool(toolId) {
      this.$emit('tool-selected', toolId);
    },

    toggleAutoConnect() {
      this.autoConnectEnabled = !this.autoConnectEnabled;
      this.$emit('auto-connect-toggled', this.autoConnectEnabled);
    },

    updateToolOptions() {
      // Emit tool options when they change
      const options = {
        connection: this.connectionOptions,
        room: this.roomOptions,
        entity: this.entityOptions
      };

      this.$emit('tool-options-changed', {
        tool: this.selectedTool,
        options
      });
    }
  }
};
</script>

<style scoped>
.world-editor-toolbar {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
}

.rail-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.rail-head .gm-label {
  margin: 0;
}

.tool-group {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

/* Two columns halve the rail's height, which is what lets every group stay
   on screen without the rail scrolling. */
.tool-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
}

.tool-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.5rem 0.3rem;
  background: var(--gm-surface-sunken);
  border: 1px solid transparent;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-muted);
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease);
}

.tool-btn:hover {
  color: var(--gm-text);
  background: var(--gm-surface-2);
}

.tool-btn:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.tool-btn.is-active {
  color: var(--gm-accent);
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
}

.tool-icon {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* The cursor arrow is a solid shape; everything else is drawn as an outline. */
.tool-btn:first-child .tool-icon {
  fill: currentColor;
  stroke: none;
}

.tool-name {
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-tight);
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
}

/* Selected-tool instruction */
.tool-hint {
  margin: 0;
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border-left: 2px solid var(--gm-border);
  border-radius: 0 var(--gm-radius-sm) var(--gm-radius-sm) 0;
  font-size: var(--gm-text-xs);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text-muted);
}

.tool-hint__label {
  display: block;
  margin-bottom: 0.15rem;
  font-family: var(--gm-font-display);
  font-size: var(--gm-text-micro);
  font-weight: 600;
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
  color: var(--gm-accent);
}

/* Auto-connect switch */
.switch-row {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  width: 100%;
  padding: 0.4rem 0.5rem;
  background: var(--gm-surface-sunken);
  border: 1px solid transparent;
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text);
  cursor: pointer;
  text-align: left;
}

.switch-row:hover {
  background: var(--gm-surface-2);
}

.switch-row:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.switch-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.switch-name {
  font-size: var(--gm-text-xs);
}

.switch-note {
  font-size: var(--gm-text-micro);
  line-height: var(--gm-leading-tight);
  color: var(--gm-text-dim);
}

.switch-track {
  position: relative;
  flex: none;
  width: 26px;
  height: 14px;
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-pill);
  transition: background var(--gm-duration) var(--gm-ease), border-color var(--gm-duration) var(--gm-ease);
}

.switch-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gm-text-dim);
  transition: transform var(--gm-duration) var(--gm-ease), background var(--gm-duration) var(--gm-ease);
}

.switch-row.is-on .switch-track {
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
}

.switch-row.is-on .switch-knob {
  background: var(--gm-accent);
  transform: translateX(12px);
}

@media (prefers-reduced-motion: reduce) {
  .tool-btn,
  .switch-track,
  .switch-knob {
    transition: none;
  }
}
</style>